// src/shared/utils/polar.ts
// Polar.sh Customer Portal API client for decentralized license key verification.

const POLAR_ENV = import.meta.env.VITE_POLAR_ENV || 'sandbox'

const POLAR_ORG_ID =
  POLAR_ENV === 'production'
    ? import.meta.env.VITE_POLAR_PRODUCTION_ORGANIZATION_ID ||
      import.meta.env.VITE_POLAR_ORGANIZATION_ID ||
      ''
    : import.meta.env.VITE_POLAR_SANDBOX_ORGANIZATION_ID ||
      import.meta.env.VITE_POLAR_ORGANIZATION_ID ||
      ''

const POLAR_API_BASE =
  POLAR_ENV === 'production'
    ? 'https://api.polar.sh/v1'
    : 'https://sandbox-api.polar.sh/v1'

export const PAYWALL_ENABLED = false

export const POLAR_CHECKOUT_URL =
  POLAR_ENV === 'production'
    ? import.meta.env.VITE_POLAR_PRODUCTION_CHECKOUT_URL ||
      import.meta.env.VITE_POLAR_CHECKOUT_URL ||
      'https://polar.sh'
    : import.meta.env.VITE_POLAR_SANDBOX_CHECKOUT_URL ||
      import.meta.env.VITE_POLAR_CHECKOUT_URL ||
      'https://polar.sh'

export interface PolarActivation {
  id: string
  createdAt: string
  modifiedAt: string
  licenseKeyId: string
  label: string
  meta: Record<string, any>
}

export interface PolarLicenseKey {
  id: string
  key: string
  status: 'granted' | 'active' | 'revoked' | 'expired' | 'disabled'
  limitActivations: number | null
  usageLimit: number | null
  usageQuantity: number
  validations: number
  lastValidatedAt: string | null
  expiresAt: string | null
  userData: Record<string, any>
  activations: PolarActivation[]
}

export interface PolarValidationResult {
  success: boolean
  licenseKey?: PolarLicenseKey
  error?: string
}

export interface PolarActivationResult {
  success: boolean
  activation?: PolarActivation
  licenseKey?: PolarLicenseKey
  error?: string
}

/**
 * Validates a license key with Polar.sh.
 */
export async function validateLicenseKey(
  key: string,
  activationId?: string,
): Promise<PolarValidationResult> {
  if (!key.trim()) {
    return { success: false, error: 'License key is empty.' }
  }
  if (!POLAR_ORG_ID) {
    return { success: false, error: 'Polar Organization ID is not configured.' }
  }

  try {
    const response = await fetch(`${POLAR_API_BASE}/customer-portal/license-keys/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        key,
        organization_id: POLAR_ORG_ID,
        ...(activationId ? { activation_id: activationId } : {}),
      }),
    })

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errData.detail || `Validation failed with status ${response.status}`,
      }
    }

    const data = await response.json()
    const isValid = data.status === 'active' || data.status === 'granted'

    if (!isValid) {
      return {
        success: false,
        error: `Subscription status is currently '${data.status}'.`,
      }
    }

    return { success: true, licenseKey: data }
  } catch (e: any) {
    console.error('[polar] validation error:', e)
    return { success: false, error: e.message || 'Network error. Please check your connection.' }
  }
}

/**
 * Fetches the customer's license keys using a Customer Session Token.
 */
export async function getCustomerLicenseKeys(token: string): Promise<string[]> {
  try {
    const response = await fetch(`${POLAR_API_BASE}/customer-portal/license-keys/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch license keys: ${response.status}`)
    }
    const data = await response.json()
    const items = Array.isArray(data)
      ? data
      : data && Array.isArray(data.items)
      ? data.items
      : []

    return items
      .filter((item: any) => item && item.key && (item.status === 'active' || item.status === 'granted'))
      .map((item: any) => item.key as string)
  } catch (e) {
    console.error('[polar] error fetching customer license keys:', e)
    return []
  }
}

/**
 * Activates a license key for this device.
 */
export async function activateLicenseKey(
  key: string,
  label: string = 'Device',
): Promise<PolarActivationResult> {
  if (!key.trim()) {
    return { success: false, error: 'License key is empty.' }
  }
  if (!POLAR_ORG_ID) {
    return { success: false, error: 'Polar Organization ID is not configured.' }
  }

  try {
    const response = await fetch(`${POLAR_API_BASE}/customer-portal/license-keys/activate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        key,
        organization_id: POLAR_ORG_ID,
        label,
      }),
    })

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errData.detail || `Activation failed with status ${response.status}`,
      }
    }

    const data = await response.json()
    return {
      success: true,
      activation: data,
      licenseKey: data.license_key,
    }
  } catch (e: any) {
    console.error('[polar] activation error:', e)
    return { success: false, error: e.message || 'Network error. Please check your connection.' }
  }
}

/**
 * Deactivates a license key activation instance.
 */
export async function deactivateLicenseKey(
  key: string,
  activationId: string,
): Promise<{ success: boolean; error?: string }> {
  if (!key.trim() || !activationId.trim()) {
    return { success: false, error: 'Key and activation ID are required.' }
  }
  if (!POLAR_ORG_ID) {
    return { success: false, error: 'Polar Organization ID is not configured.' }
  }

  try {
    const response = await fetch(`${POLAR_API_BASE}/customer-portal/license-keys/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        key,
        organization_id: POLAR_ORG_ID,
        activation_id: activationId,
      }),
    })

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errData.detail || `Deactivation failed with status ${response.status}`,
      }
    }

    return { success: true }
  } catch (e: any) {
    console.error('[polar] deactivation error:', e)
    return { success: false, error: e.message || 'Network error. Please check your connection.' }
  }
}
